<template>
  <div
    v-show="showToolbar"
    :class="['view-toolbar', $store.state.breakpoint.smAndDown ? '-mobile' : '']"
  >
    <div class="_left">
      <div
        v-if="showLeftGradient"
        class="_gradient -left"
      />
      <div
        class="_scroller"
        @scroll="handleScroll"
      >
        <sorter-chips
          v-if="showSorter"
          ref="sorter"
          :value="order"
          :sort-options="sortOptions"
          :disabled="viewsService.context.readOnly.value"
          class="_btn"
          :has-unsaved-changes="viewsService.currentViewUnsavedChanges.value.includes('order')"
          @input="updateViewParams($event, 'order')"
        />
        <div
          v-if="showSorter && showFilter"
          class="_divider"
        />
        <filter-chips
          v-if="showFilter"
          :props="filterProps"
          :filter="filter"
          :quick-filters="currentView.params?.quickFilters"
          :has-dynamic-dates="hasDynamicDateFilters"
          :unsaved-changes="viewsService.currentViewUnsavedChanges.value"
          :multi-model="multiModel"
          :disabled="viewsService.context.readOnly.value"
          class="_filter-chips"
          @update:filter="updateViewParams($event, 'filter')"
          @update:quick-filters="updateViewParams($event, 'quickFilters')"
        />
        <m-dropdown
          v-if="showFilter && filterHasValue"
          :title="$t('general.actions')"
        >
          <m-btn
            icon="ellipsis"
            fab
            hide-border
            small
            light
          />
          <template #overlay>
            <m-card
              list
            >
              <apply-filter-on-first-level-only
                :value="currentView.params?.applyFilterOnFirstLevelOnly"
                :disabled="viewsService.context.readOnly.value"
                @update:value="updateViewParams($event, 'applyFilterOnFirstLevelOnly')"
              />
            </m-card>
          </template>
        </m-dropdown>
      </div>
      <div
        v-if="showRightGradient"
        class="_gradient -right"
      />
    </div>
    <div
      v-if="!viewsService.context.readOnly.value && viewsService.currentViewHasUnsavedChanges.value && $store.state.breakpoint.smAndDown"
      class="_right"
    >
      <save-view-actions
        :views-service="viewsService"
      />
    </div>
  </div>
</template>

<script setup>
import ApplyFilterOnFirstLevelOnly from '@/components/filter/ApplyFilterOnFirstLevelOnly.vue';
import FilterChips from '@/components/filter/FilterChips.vue';
import SaveViewActions from '@/components/list/SaveViewActions.vue';
import SorterChips from '@/components/sorter/SorterChips.vue';
import useViewToolbarVisibility from '@/composables/view-toolbar/view-toolbar-visibility';
import { VIEW_EVENT_BUS } from '@/lib/constants';
import { computed, inject, nextTick, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  sortOptions: {
    type: Array,
    default: () => [],
  },
  filterProps: {
    type: Array,
    default: () => [],
  },
  multiModel: {
    type: Boolean,
    default: false,
  },
  hasDynamicDateFilters: {
    type: Boolean,
    default: false,
  },
  viewsService: {
    type: Object,
    required: true,
  },
  canFilter: {
    type: Boolean,
    default: true,
  },
  canSort: {
    type: Boolean,
    default: true,
  },
});

const eventBus = inject(VIEW_EVENT_BUS);

const sorter = ref(null);

const currentView = props.viewsService.currentView;

const { shown: showToolbarFlag } = useViewToolbarVisibility(currentView);

const filter = computed(() => currentView.value.params?.filter ?? null);
const order = computed(() => currentView.value.params?.order ?? []);
const showSorter = computed(() => props.canSort && (order.value.length > 0 || (sorter.value !== undefined && sorter.value !== null && sorter.value.visible)));
const showFilter = computed(() => props.canFilter);

const filterHasValue = computed(() => filter.value.children.length > 0);

const updateViewParams = (value, key) => {
  props.viewsService.setParams(
    currentView.value,
    {
      ...currentView.value.params,
      [key]: value,
    },
  );
};

const showRightGradient = ref(true);
const showLeftGradient = ref(false);

const store = useStore();

const showToolbar = computed(() => store.state.breakpoint.smAndDown || showToolbarFlag.value);

const handleScroll = (event) => {
  if (event.target.scrollLeft > 0) {
    showLeftGradient.value = true;
  } else {
    showLeftGradient.value = false;
  }
  // Remove it before it reaches the end of the scrollbar (18px is the width of the gradient)
  if (event.target.scrollLeft + event.target.clientWidth > event.target.scrollWidth - 18) {
    showRightGradient.value = false;
  } else {
    showRightGradient.value = true;
  }
};

const toggleShowSort = () => {
  showToolbarFlag.value = !showToolbarFlag.value;
  if (showToolbarFlag.value) {
    nextTick(() => {
      sorter.value.showDropdown();
    });
  }
};

const handleSortOptionClick = (option) => {
  showToolbarFlag.value = true;
  updateViewParams([{ ...option.value, desc: false }], 'order');
  nextTick(() => {
    sorter.value.showDropdown();
  });
};

const toggleViewToolbar = (option) => {
  if (option.toolbarAction === 'sorter') {
    toggleShowSort();
    return;
  }
  showToolbarFlag.value = !showToolbarFlag.value;
};

const showViewToolbar = () => {
  showToolbarFlag.value = true;
};

eventBus.$on('toggle-view-toolbar', toggleViewToolbar);
eventBus.$on('show-view-toolbar', showViewToolbar);
eventBus.$on('add-sort', handleSortOptionClick);

onUnmounted(() => {
  eventBus.$off('toggle-view-toolbar', toggleViewToolbar);
  eventBus.$off('add-sort', handleSortOptionClick);
  eventBus.$off('show-view-toolbar', showViewToolbar);
});
</script>

<style lang="scss" scoped>
.view-toolbar {
  display: flex;
  overflow-x: auto;

  ._divider {
    flex: 0 0 1px;
    height: auto;
    background-color: $border-color;
  }

  ._left {
    flex: 1 1 auto;
    position: relative;
    overflow: hidden;

    ._scroller {
      padding-top: 1rem;
      padding-bottom: 1rem;
      padding-right: 1.6rem;
      scrollbar-color: map_get($grey, 'lighten-2') rgba(0, 0, 0, 0);
      overflow-x: auto;
      width: 100%;
      display: flex;
      gap: 0.8rem;
    }

    ._gradient {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 1.8rem;
      z-index: 10;
      pointer-events: none;

      &.-right {
        right: 0;
        background: linear-gradient(270deg, white 0%, rgba(255, 255, 255, 0) 100%);
      }

      &.-left {
        left: 0;
        background: linear-gradient(90deg, white 0%, rgba(255, 255, 255, 0) 100%);
      }
    }
  }

  ._right {
    padding-left: .8rem;
    padding-bottom: 1rem;
    margin-left: auto;
    flex: 0 0 auto;
  }

  &.-mobile {
    flex-wrap: wrap;

    ._right {
      margin-left: 0;
      padding-left: 0;
      width: 100%;
    }
  }
}
</style>
